import {
  Box,
  ButtonGroup,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { ChangeEvent, useContext, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addCommentAsync } from '../../services/commentsService'
import { RootState } from '../../store'
import { ConstructionTab } from '../../reducers/construction'
import { commentsCursor } from '../../models/Comment'
import {
  setIsAddCommentMode,
  updateAddCommentInfo,
} from '../../actions/comments'
import { AbilityContext, Can } from '../../utils/AbilityUtil'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'

interface AddCommentProps {
  tab: ConstructionTab
  position: { x: number; y: number }
  header?: {
    component: JSX.Element
    height: number
  }
}

const AddCommentComponent: React.FC<AddCommentProps> = ({
  tab,
  position,
  header,
}) => {
  const dispatch = useDispatch<any>()
  const [commentText, setCommentText] = useState('')
  const state = useSelector((state: RootState) => state)
  const file = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )
  const isAddCommentMode = useSelector(
    (state: RootState) => state.comments.isAddCommentMode,
  )
  const addCommentDiscipline = useSelector(
    (state: RootState) => state.comments.addCommentInfo.commentDiscipline,
  )
  const author = state.auth.user

  const headerRef = useRef<HTMLDivElement | null>(null)

  const ability = useContext(AbilityContext)

  const handleCommentChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCommentText(event.target.value)
  }

  const handleAddCommentClick = () => {
    dispatch(setIsAddCommentMode(false))
    if (!!author) {
      dispatch(
        addCommentAsync({
          tab,
          comment: {
            content: commentText,
            file_id: file?.id,
            author_id: author?.id,
            positionX: position.x,
            positionY: position.y,
            discipline: addCommentDiscipline,
          },
        }),
      )
    } else {
      console.error('Error: can not add comment, author is unknown')
    }
    dispatch(
      updateAddCommentInfo({
        commentDiscipline: null,
        position: { x: 0, y: 0 },
      }),
    )
  }

  return (
    <Can I={AttributeActionNames.Create} a={AttributeResourceNames.Comments}>
      {isAddCommentMode && (
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            top: header ? position.y - header?.height : position.y,
            left: position.x,
            zIndex: 100,
          }}
        >
          <Box
            sx={{
              cursor: `url(${commentsCursor}) 0 20, auto`,
              pr: '5px',
              pt: `${header ? header.height : 0}px`,
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='33'
              height='33'
              viewBox='0 0 33 33'
              fill='none'
            >
              <circle cx='16.5' cy='16.5' r='16.5' fill='#1705E5' />
              <rect y='16' width='17' height='17' fill='#1705E5' />
            </svg>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!!header ? <div ref={headerRef}>{header.component}</div> : <></>}
            <Box
              component='form'
              sx={{
                width: '291px',
                backgroundColor: '#F2F2F2',
                borderRadius: '4px',
                p: '11px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#B5B5B5',
                }}
              >
                Add a comment
              </Typography>
              <TextField
                value={commentText}
                disabled={ability.cannot(
                  AttributeActionNames.Create,
                  AttributeResourceNames.Comments,
                )}
                onChange={handleCommentChange}
                fullWidth
                autoFocus
                size='small'
                variant='standard'
                multiline
                sx={{
                  color: '#B5B5B5',
                  fontSize: '12px',
                  lineHeight: 'normal',
                  m: '5px 0',
                  '& .MuiInputBase-input': {
                    color: '#212121',
                    fontSize: '14px',
                    lineHeight: '22px',
                    border: 'none',
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <ButtonGroup>
                  <IconButton disableRipple sx={{ p: '5px 10px 0 0' }}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                      fill='none'
                    >
                      <path
                        d='M8.63158 10.2523C7.86721 10.7263 6.96557 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 7.0701 10.4682 8.17524 9.22783 8.06248C8.2074 7.96972 7.47291 7.04096 7.61782 6.02662L7.9041 4.05556M7.58784 6.25971C7.42165 7.44222 6.44814 8.28295 5.41344 8.13753C4.37875 7.99212 3.67469 6.91562 3.84088 5.73311C4.00707 4.5506 4.98058 3.70988 6.01527 3.85529C7.04997 4.00071 7.75403 5.07721 7.58784 6.25971Z'
                        stroke='black'
                        strokeWidth='0.75'
                        strokeLinecap='square'
                      />
                    </svg>
                  </IconButton>
                  <IconButton disableRipple sx={{ p: '5px 10px 0 0' }}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='10'
                      height='10'
                      viewBox='0 0 10 10'
                      fill='none'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z'
                        fill='black'
                      />
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M2.67773 5.97922C2.7495 5.93778 2.8348 5.92655 2.91485 5.948C2.9949 5.96945 3.06316 6.02182 3.1046 6.09359C3.29654 6.4263 3.57276 6.70257 3.90543 6.89458C4.2381 7.08659 4.61549 7.18757 4.9996 7.18734C5.38371 7.18757 5.7611 7.08659 6.09377 6.89458C6.42644 6.70257 6.70266 6.4263 6.8946 6.09359C6.91497 6.05777 6.94222 6.02632 6.9748 6.00107C7.00737 5.97582 7.04461 5.95726 7.08438 5.94646C7.12416 5.93567 7.16567 5.93284 7.20654 5.93815C7.24741 5.94347 7.28682 5.95681 7.32252 5.97742C7.35821 5.99802 7.38947 6.02549 7.4145 6.05822C7.43954 6.09096 7.45785 6.12833 7.46838 6.16817C7.47892 6.20801 7.48146 6.24955 7.47588 6.29038C7.4703 6.33121 7.45669 6.37054 7.43585 6.40609C7.18905 6.83381 6.83394 7.18898 6.40626 7.43584C5.97858 7.68271 5.49342 7.81257 4.9996 7.81234C4.50578 7.81257 4.02062 7.68271 3.59294 7.43584C3.16526 7.18898 2.81015 6.83381 2.56335 6.40609C2.52191 6.33432 2.51068 6.24902 2.53213 6.16897C2.55358 6.08891 2.60595 6.02066 2.67773 5.97922Z'
                        fill='black'
                      />
                      <path
                        d='M4.375 4.0625C4.375 4.58 4.095 5 3.75 5C3.405 5 3.125 4.58 3.125 4.0625C3.125 3.545 3.405 3.125 3.75 3.125C4.095 3.125 4.375 3.545 4.375 4.0625ZM6.875 4.0625C6.875 4.58 6.595 5 6.25 5C5.905 5 5.625 4.58 5.625 4.0625C5.625 3.545 5.905 3.125 6.25 3.125C6.595 3.125 6.875 3.545 6.875 4.0625Z'
                        fill='black'
                      />
                    </svg>
                  </IconButton>
                  <IconButton disableRipple sx={{ p: '5px 10px 0 0' }}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                      fill='none'
                    >
                      <path
                        d='M1 7.38157L2.77805 6.05134C3.28835 5.71114 3.97009 5.79141 4.38742 6.24084C5.32019 7.24536 6.40161 8.15861 7.875 8.15861C9.23293 8.15861 10.1328 7.65532 11 6.7881M1 1H11V11H1V1ZM9.125 4.125C9.125 4.81536 8.56536 5.375 7.875 5.375C7.18464 5.375 6.625 4.81536 6.625 4.125C6.625 3.43464 7.18464 2.875 7.875 2.875C8.56536 2.875 9.125 3.43464 9.125 4.125Z'
                        stroke='black'
                        strokeWidth='0.75'
                      />
                    </svg>
                  </IconButton>
                </ButtonGroup>
                <IconButton
                  disableRipple
                  disabled={ability.cannot(
                    AttributeActionNames.Create,
                    AttributeResourceNames.Comments,
                  )}
                  onClick={handleAddCommentClick}
                  sx={{ p: '5px 2px 0 0' }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='14'
                    viewBox='0 0 15 14'
                    fill='none'
                  >
                    <path
                      d='M9.88797 1L14 5.0877M14 5.0877L9.88797 9.1754M14 5.0877L5 5.0877C2.79086 5.0877 1 6.87856 1 9.0877L1 13'
                      stroke='#1705E5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Can>
  )
}

export default AddCommentComponent
